import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subtitle, Title } from "capitalrx-components-2.0/lib/typography";
import styled from "styled-components";

import { BlankPageWrapper, SectionPanel } from "@/App/components";
import { FindBestPriceWidget } from "@/App/components";
import { ExperienceContext } from "@/App/context";
import { MemberEligibilityContext } from "@/App/context/MemberEligibilityContext";

import { PharmacySearchCard } from "../PharmacySearch";
import { AdditionalServicesTiles } from "./AdditionalServicesTiles";
import { JudiCareTile } from "./JudiCareTile";
import { NeedHelpTile } from "./NeedHelpTile";
import { QuickActions } from "./QuickActions";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard";

const PlaceholderContent = styled.div`
  color: #9ca3af;
  font-style: italic;
  padding: 40px;
  text-align: center;
  border: 2px dashed #e5e7eb;
  border-radius: 8px;
`;

export const RecentActivityDashboardPage: FC = () => {
  const { t: translate } = useTranslation();
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { hidePlanContributions } = useContext(ExperienceContext);

  const { leftColumnWidth, rightColumnWidth } = useResponsive({
    leftColumnWidth: {
      desktop: 8,
      tablet: 12,
      mobile: 12,
    },
    rightColumnWidth: {
      desktop: 4,
      tablet: 12,
      mobile: 12,
    },
  });

  return (
    <BlankPageWrapper>
      <Stack spacing="large">
        {/* Welcome Header */}
        <Title testId="welcome-header">
          {translate(`${TRANSLATION_PATH}.welcomeHeader`)}
          {memberEligibility.member.first_name || ""}
        </Title>

        {/* Quick Actions */}
        <QuickActions />

        {/* Two-Column Layout */}
        <Grid>
          {/* Left Column - Recent Activity */}
          <Grid.Span columns={leftColumnWidth}>
            <Stack spacing="large">
              <Stack spacing="small">
                <Subtitle testId="recent-activity-title">{translate(`${TRANSLATION_PATH}.recentActivityTitle`)}</Subtitle>
                <Body>{translate(`${TRANSLATION_PATH}.recentActivitySubtitle`)}</Body>
                <SectionPanel>
                  <PlaceholderContent>{translate(`${TRANSLATION_PATH}.recentActivityPlaceholder`)}</PlaceholderContent>
                </SectionPanel>
              </Stack>

              {/* JudiCare Tile */}
              <JudiCareTile />
            </Stack>
          </Grid.Span>

          {/* Right Column - Widgets */}
          <Grid.Span columns={rightColumnWidth}>
            <Stack spacing="large">
              {/* Plan Contributions Placeholder */}
              {!hidePlanContributions && (
                <Stack spacing="small">
                  <Subtitle testId="plan-contributions-title">{translate(`${TRANSLATION_PATH}.planContributionsTitle`)}</Subtitle>
                  <SectionPanel>
                    <PlaceholderContent>{translate(`${TRANSLATION_PATH}.planContributionsPlaceholder`)}</PlaceholderContent>
                  </SectionPanel>
                </Stack>
              )}

              {/* Find Best Price Placeholder */}
              <Stack spacing="small">
                <Subtitle testId="find-best-price-title">{translate(`${TRANSLATION_PATH}.findBestPriceTitle`)}</Subtitle>
                <SectionPanel>
                  <FindBestPriceWidget />
                </SectionPanel>
              </Stack>

              {/* Find Nearby Pharmacies */}
              <PharmacySearchCard />

              {/* Need Help Tile */}
              <NeedHelpTile />
            </Stack>
          </Grid.Span>
        </Grid>

        {/* Additional Services (Costco) - Full Width */}
        <AdditionalServicesTiles />
      </Stack>
    </BlankPageWrapper>
  );
};
